import { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { sb } from '../lib/supabase';
import { useSupabaseAuth, useProgrammeAccess } from '../context/AuthContext';
import Badge from '../components/Badge';
import Loader from '../components/Loader';
import Modal from '../components/Modal';

const ACCESS_ROLES = [
  { value: 'admin', label: 'Programme admin' },
  { value: 'member', label: 'Member' },
  { value: 'viewer', label: 'Viewer' },
];

const ACCESS_BADGE = { admin: 'pu', member: 'bl', viewer: '' };
const ACCESS_LABEL = { admin: 'Admin', member: 'Member', viewer: 'Viewer' };

function initials(name) {
  const parts = (name || '?').trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

export default function ProgrammeMembers() {
  const { programme, reload: reloadShell } = useOutletContext();
  const { profile } = useSupabaseAuth();
  const access = useProgrammeAccess(programme?.id);

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [newRole, setNewRole] = useState('member');
  const [roleBusy, setRoleBusy] = useState(null);

  const loadMembers = useCallback(async () => {
    if (!programme?.id) return;
    setLoading(true);
    setError('');
    const { data, error: err } = await sb
      .from('programme_members')
      .select('programme_id, profile_id, access_role, role, profiles(id, full_name, email, job_title)')
      .eq('programme_id', programme.id);
    if (err) setError(err.message);
    const rows = (data || []).slice().sort((a, b) => {
      const rank = { admin: 0, member: 1, viewer: 2 };
      const dr = (rank[a.access_role] ?? 9) - (rank[b.access_role] ?? 9);
      if (dr !== 0) return dr;
      return (a.profiles?.full_name || '').localeCompare(b.profiles?.full_name || '');
    });
    setMembers(rows);
    setLoading(false);
  }, [programme?.id]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const adminCount = useMemo(
    () => members.filter((m) => m.access_role === 'admin').length,
    [members],
  );

  const memberIds = useMemo(() => new Set(members.map((m) => m.profile_id)), [members]);

  const loadCandidates = useCallback(async (q) => {
    if (!programme?.organisation_id) return;
    let query = sb
      .from('profiles')
      .select('id, full_name, email, job_title')
      .eq('organisation_id', programme.organisation_id)
      .order('full_name')
      .limit(40);
    const term = q.trim();
    if (term) query = query.or(`full_name.ilike.%${term}%,email.ilike.%${term}%`);
    const { data } = await query;
    setCandidates((data || []).filter((p) => !memberIds.has(p.id)));
  }, [programme?.organisation_id, memberIds]);

  useEffect(() => {
    if (!showAdd) return;
    loadCandidates(search);
  }, [showAdd, search, loadCandidates]);

  async function addMember() {
    if (!selectedProfileId || !programme?.id) return;
    setSaving(true);
    setError('');
    const { error: err } = await sb.from('programme_members').insert({
      programme_id: programme.id,
      profile_id: selectedProfileId,
      access_role: newRole,
      role: 'consultant',
    });
    setSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    setShowAdd(false);
    setSelectedProfileId('');
    setNewRole('member');
    setSearch('');
    await loadMembers();
    reloadShell?.();
  }

  async function changeRole(profileId, accessRole) {
    if (!programme?.id) return;
    setRoleBusy(profileId);
    setError('');
    const { error: err } = await sb
      .from('programme_members')
      .update({ access_role: accessRole })
      .eq('programme_id', programme.id)
      .eq('profile_id', profileId);
    setRoleBusy(null);
    if (err) {
      setError(err.message);
      return;
    }
    await loadMembers();
  }

  async function removeMember(row) {
    if (!programme?.id) return;
    const isSelf = row.profile_id === profile?.id;
    const isLastAdmin = row.access_role === 'admin' && adminCount <= 1;
    if (isSelf && isLastAdmin) {
      setError('You cannot remove yourself while you are the only programme admin.');
      return;
    }
    if (!window.confirm(`Remove ${row.profiles?.full_name || 'this person'} from the programme?`)) return;
    setSaving(true);
    setError('');
    const { error: err } = await sb
      .from('programme_members')
      .delete()
      .eq('programme_id', programme.id)
      .eq('profile_id', row.profile_id);
    setSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    await loadMembers();
    reloadShell?.();
  }

  if (access.loading) return <Loader text="Checking access…" />;

  if (!access.isAdmin) {
    return (
      <div className="content">
        <div className="empty" style={{ marginTop: 40 }}>
          Programme member management is limited to programme admins.
        </div>
      </div>
    );
  }

  return (
    <div className="content">
      <div className="sec-hdr">
        <div>
          <span className="sec-title">Programme members</span>
          <div style={{ fontSize: 11, color: '#888780', marginTop: 4 }}>
            Control who can view or edit {programme.name}. Project access inherits from programme membership.
          </div>
        </div>
        <button type="button" className="btn-primary" onClick={() => setShowAdd(true)} disabled={saving}>
          + Add member
        </button>
      </div>

      {error && (
        <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 8, background: '#FCE8E8', color: '#A32D2D', fontSize: 12 }}>
          {error}
        </div>
      )}

      {loading ? (
        <Loader />
      ) : members.length === 0 ? (
        <div className="empty">No members yet.</div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Person</th>
                <th>Access</th>
                <th style={{ width: 100 }} />
              </tr>
            </thead>
            <tbody>
              {members.map((row) => {
                const p = row.profiles;
                const isSelf = row.profile_id === profile?.id;
                const isLastAdmin = row.access_role === 'admin' && adminCount <= 1;
                return (
                  <tr key={row.profile_id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div className="ua-avatar">{initials(p?.full_name)}</div>
                        <div>
                          <div style={{ fontWeight: 500 }}>{p?.full_name || '—'}</div>
                          <div style={{ fontSize: 11, color: '#888780' }}>{p?.email || p?.job_title || '—'}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <Badge variant={ACCESS_BADGE[row.access_role] || ''}>
                          {ACCESS_LABEL[row.access_role] || row.access_role}
                        </Badge>
                        <select
                          className="form-select"
                          style={{ width: 160, fontSize: 12 }}
                          value={row.access_role}
                          disabled={roleBusy === row.profile_id || saving}
                          onChange={(e) => changeRole(row.profile_id, e.target.value)}
                        >
                          {ACCESS_ROLES.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                      </div>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="row-act row-act-close"
                        disabled={saving || (isSelf && isLastAdmin)}
                        title={isSelf && isLastAdmin ? 'Cannot remove the only programme admin' : 'Remove from programme'}
                        onClick={() => removeMember(row)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && (
        <Modal
          title="Add programme member"
          onClose={() => !saving && setShowAdd(false)}
          busy={saving}
          footer={(
            <>
              <button type="button" className="btn-ghost" onClick={() => setShowAdd(false)} disabled={saving}>Cancel</button>
              <button type="button" className="btn-primary" onClick={addMember} disabled={saving || !selectedProfileId}>Add</button>
            </>
          )}
        >
          <div className="form-row">
            <label className="form-label">Search people</label>
            <input
              className="form-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name or email"
            />
          </div>
          <div className="form-row">
            <label className="form-label">Person</label>
            <div style={{ maxHeight: 220, overflowY: 'auto', border: '0.5px solid #d3d1c7', borderRadius: 8 }}>
              {candidates.length === 0 ? (
                <div className="empty" style={{ padding: 16 }}>No matching people in your organisation.</div>
              ) : (
                candidates.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className={`ra-resource-option${selectedProfileId === p.id ? ' selected' : ''}`}
                    style={{ width: '100%', textAlign: 'left' }}
                    onClick={() => setSelectedProfileId(p.id)}
                  >
                    <div className="ra-resource-av">{initials(p.full_name)}</div>
                    <div>
                      <div className="ra-resource-name">{p.full_name}</div>
                      <div className="ra-resource-meta">{p.email || p.job_title || '—'}</div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
          <div className="form-row">
            <label className="form-label">Access role</label>
            <select className="form-select" value={newRole} onChange={(e) => setNewRole(e.target.value)}>
              {ACCESS_ROLES.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
        </Modal>
      )}
    </div>
  );
}
